import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// Shared nerdle labs infrastructure — identical think-aloud capture stack as the 24 game.
import { AdLayout } from '../../shared/components/AdLayout'
import { BrowserGate } from '../../shared/components/BrowserGate'
import { Brand } from '../../shared/components/Brand'
import { ReflectionNotice } from '../../shared/components/ReflectionNotice'
import { InstantRetro, SolutionType } from '../../shared/components/InstantRetro'
import { StatusScreen } from '../../shared/components/StatusScreen'
import { browserGateReason, captureEnv, enrichCountry, CaptureEnv } from '../../shared/lib/env'
import { uuidv4 } from '../../shared/lib/uuid'
import { useSpeechRecognition, TranscriptEntry } from '../../shared/labs/useSpeechRecognition'
import { useStreamingTranscription } from '../../shared/labs/useStreamingTranscription'
import { getSttEngine } from '../../shared/labs/sttEngine'
import { getSourceTag } from '../../shared/labs/sourceTag'
import { useThinkingEnergy, ENERGY_CONFIG } from '../../shared/labs/useThinkingEnergy'
import { InteractionEvent, CaptureMode } from '../../shared/labs/types'
import { saveConsent, saveCrypticTrace } from '../../shared/labs/sessionStore'
import { ConfirmedSegment, formatWithPauses } from '../../shared/labs/transcript'
import { track, setLabsUser } from '../../shared/labs/analytics'
import { addStreamedMs, overDailyCap } from '../../shared/labs/streamLimit'
import { isTrivialSolve } from '../../shared/labs/trivialSolve'

// Cryptic-specific UI + logic.
import { CrypticConsent } from './components/CrypticConsent'
import { CrypticView } from './components/CrypticView'
import { CrypticStage } from './components/CrypticStage'
import { CrypticAnswer } from './components/CrypticAnswer'
import {
  buildCrypticTest,
  buildCrypticTestFromConditions,
  pickCrypticPlay,
  pickCrypticPractice,
  checkCrypticAnswer,
  detectCrypticAnswer,
  CrypticSessionPuzzle,
} from './cryptic/puzzles'
import { hasCompletedAnyLabsTest, hasConsented, markConsented } from '../../shared/labs/progress'
import { OtherGamesLinks } from '../../shared/components/OtherGamesLinks'
import { SurveyScreen } from '../../shared/labs/SurveyScreen'
import { surveyCompletedAt, markSurveyDone } from '../../shared/labs/survey'
import { scrollClearingStickyAdSettling } from '../../shared/labs/useScrollIntoView'
import { CrypticTrace, CRYPTIC_TRACE_SCHEMA_VERSION, CrypticAnswerMode } from './cryptic/trace'
import { CrypticVariant, STANDARD_CRYPTIC } from './variant'

const REFLECTION_SECONDS = 5
const STT_ENGINE = getSttEngine()
const SOURCE = getSourceTag()
const PUZZLE_LIMIT_MS = 3 * 60 * 1000 // Wurgaft's 3-minute per-puzzle limit → auto-end unsolved
const NUDGE_SILENCE_MS = 3000
const COMMIT_WORD = /\bcommit\b/i
const RETRO_DONE_WORD = /\b(done|commit|finished)\b/i // hands-free submit of the instant-solve retro
const TEST_LEN = 10 // cryptic test length (matches 24; see LABS_ROADMAP)

// localStorage: has the player completed this arm's test (which unlocks free play)? Keyed per
// variant so finishing the standard cryptic doesn't unlock the easier one (and vice versa).
const isTestDone = (key: string): boolean => {
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}
const markTestDone = (key: string) => {
  try {
    localStorage.setItem(key, '1')
  } catch {
    /* noop */
  }
}

// Per-device, per-variant seen-set of clue ids → firstExposure flag (each PhD LLM saw every clue
// once; restricting human analysis to first exposures keeps the arms comparable).
const seenIds = (key: string): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem(key) || '[]'))
  } catch {
    return new Set()
  }
}
const markSeen = (key: string, id: string) => {
  try {
    const s = seenIds(key)
    s.add(id)
    localStorage.setItem(key, JSON.stringify(Array.from(s)))
  } catch {
    /* noop */
  }
}

// Amber definition underline — same class as CrypticStage so clue + Meaning match.
const DEF_UNDERLINE = 'underline decoration-2 underline-offset-[3px] decoration-[#e0a53a] font-medium'

// Human labels for the wordplay device (the reveal's "Wordplay" tag). 'other' → no label.
const DEVICE_LABEL: Record<string, string> = {
  charade: 'Charade',
  container: 'Container',
  anagram: 'Anagram',
  'double-definition': 'Double def',
  'cryptic-definition': 'Cryptic def',
  deletion: 'Deletion',
  reversal: 'Reversal',
  homophone: 'Homophone',
  hidden: 'Hidden',
  acrostic: 'Acrostic',
}

// Outcome markers — inline line-icons, no emoji (a give-up shows no icon at all).
const OutcomeIcon = ({ kind, className }: { kind: 'solved' | 'wrong' | 'timeout'; className?: string }) => {
  const cfg = {
    solved: { c: 'text-[#398874]', d: <path d="M4 12.5l5 5L20 6.5" /> },
    wrong: { c: 'text-[#820458]', d: <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" /> },
    timeout: {
      c: 'text-gray-400',
      d: (
        <>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 7.3V12l3.2 2.1" />
        </>
      ),
    },
  }[kind]
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`${className || 'mx-auto h-8 w-8'} ${cfg.c}`}
      aria-hidden="true"
    >
      {cfg.d}
    </svg>
  )
}

type Phase =
  | 'consent'
  | 'mic-error'
  | 'daily-limit'
  | 'mode-choice'
  | 'practice-intro'
  | 'test-intro'
  | 'playing'
  | 'reflection'
  | 'retro' // instant-solve retrospective (replaces reflection when the solve was trivial)
  | 'entry'
  | 'reveal' // answer + explanation, shown after EVERY outcome (solved / wrong / pass / timeout)
  | 'summary'
  | 'survey' // optional end-of-play feedback survey (skippable)

function GameCryptic({ variant = STANDARD_CRYPTIC }: { variant?: CrypticVariant } = {}) {
  const gateReason = useMemo(() => browserGateReason(), [])

  const [phase, setPhase] = useState<Phase>('consent')
  const [confirmedSegments, setConfirmedSegments] = useState<ConfirmedSegment[]>([])
  const [interimText, setInterimText] = useState('')
  const [tsBase, setTsBase] = useState(0)
  const [saveError, setSaveError] = useState<string | null>(null)
  // The just-finished clue's outcome, shown on the reveal screen (every ending lands here).
  const [outcome, setOutcome] = useState<{ answerMode: CrypticAnswerMode; correct: boolean; typed: string | null } | null>(null)
  const [showPassConfirm, setShowPassConfirm] = useState(false)
  const [surveyDismissed, setSurveyDismissed] = useState(false) // "No thanks" on the summary feedback prompt
  // ---- instant-solve retrospective (aha payoff) ----
  // Retro speech flows into the main transcript (tagged 'retro'); only the self-report tap is local.
  const [solutionType, setSolutionType] = useState<SolutionType>(null) // self-reported insight/analytic

  // ---- mode/session state ----
  const [mode, setMode] = useState<CaptureMode>('test')
  const [sessionPuzzles, setSessionPuzzles] = useState<CrypticSessionPuzzle[]>([])
  const [puzzleIndex, setPuzzleIndex] = useState(0)
  const puzzle = sessionPuzzles[puzzleIndex] as CrypticSessionPuzzle | undefined
  const isLastInSession = puzzleIndex >= sessionPuzzles.length - 1
  const progressLabel =
    mode === 'test'
      ? `Clue ${puzzleIndex + 1} of ${sessionPuzzles.length}`
      : mode === 'practice'
      ? 'Warm-up'
      : `Free play · clue ${puzzleIndex + 1}`

  // ---- refs (per-puzzle capture buffers) ----
  const sessionRef = useRef<{ id: string; startedAtISO: string } | null>(null)
  const envRef = useRef<CaptureEnv | null>(null)
  const transcriptRef = useRef<TranscriptEntry[]>([])
  const eventsRef = useRef<InteractionEvent[]>([])
  const puzzleStartRef = useRef<{ iso: string; perf: number } | null>(null)
  const committedTranscriptRef = useRef<string>('')
  const interimRef = useRef<{ text: string; ts: number }>({ text: '', ts: 0 }) // last unfinalized words (for commit capture)
  const commitTimerRef = useRef<number | null>(null) // spoken-commit: wait for the utterance to finalize
  const pendingCommitRef = useRef(false)
  const hasCommittedRef = useRef(false)
  const committedAtRef = useRef<number>(0)
  const reflectionEndRef = useRef<number>(0)
  const dimRef = useRef(false)
  const sampleCounterRef = useRef(0)
  const lastActivityRef = useRef(0)
  const tsBaseRef = useRef(0)
  const sessionTracesRef = useRef<CrypticTrace[]>([])
  const endUnsolvedRef = useRef<(reason: 'pass' | 'timeout') => void>(() => {})
  // retro capture (kept off the puzzle transcript so it never contaminates the trace stream)
  const trivialSolveRef = useRef(false)
  const matchedAtCommitRef = useRef<string | null>(null) // correct answer heard at commit (trivial solve → auto-credit)
  const recordTrivialRef = useRef<() => void>(() => {})
  const solutionTypeRef = useRef<SolutionType>(null)
  const endRetroRef = useRef<() => void>(() => {})
  // live mirrors so the committed callbacks read the current puzzle/mode without stale closures
  const puzzleRef = useRef<CrypticSessionPuzzle | undefined>(undefined)
  puzzleRef.current = puzzle
  const modeRef = useRef<CaptureMode>('test')
  modeRef.current = mode
  const phaseRef = useRef<Phase>('consent') // live phase for the transcription callback (never stale)
  phaseRef.current = phase

  const pushEvent = useCallback((e: InteractionEvent) => {
    eventsRef.current.push(e)
  }, [])

  useEffect(() => {
    track('labs_cryptic_consent_view')
  }, [])

  // Reveal is taller than the retro/reflection it replaces, so bring its primary button above the
  // sticky bottom ad when we land on it (it otherwise inherits the prior screen's scroll position).
  const revealBtnRef = useRef<HTMLDivElement>(null) // wraps the reveal's actions; scroll reveals down to the switcher
  useEffect(() => {
    if (phase !== 'reveal') return
    return scrollClearingStickyAdSettling(revealBtnRef.current)
  }, [phase])

  // clear any pending spoken-commit timer on unmount
  useEffect(() => () => { if (commitTimerRef.current != null) clearTimeout(commitTimerRef.current) }, [])

  // Wurgaft's 3-minute per-puzzle limit (identical to the 24 game): arm a timer for the
  // remaining time; if it fires the trial ends unsolved ('timeout'). Committing leaves
  // 'playing', clearing it via cleanup. Re-checks on tab wake so a suspended timer can't
  // let an abandoned puzzle record hours.
  useEffect(() => {
    if (phase !== 'playing') return
    const start = puzzleStartRef.current?.perf ?? performance.now()
    const fireIfOver = () => {
      if (performance.now() - start >= PUZZLE_LIMIT_MS) endUnsolvedRef.current('timeout')
    }
    const remaining = Math.max(0, PUZZLE_LIMIT_MS - (performance.now() - start))
    const id = window.setTimeout(fireIfOver, remaining)
    const onVisible = () => {
      if (document.visibilityState === 'visible') fireIfOver()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearTimeout(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [phase, puzzleIndex])

  // ---- thinking-energy mechanic (identical config to the 24 game) ----
  const energy = useThinkingEnergy({
    running: phase === 'playing' || phase === 'reflection',
    tsBase,
    resetKey: `${mode}-${puzzleIndex}`,
    onSample: (s) => {
      const nowDim = s.energy <= ENERGY_CONFIG.dimThreshold
      if (nowDim !== dimRef.current) {
        dimRef.current = nowDim
        pushEvent({ ts: s.ts, type: nowDim ? 'dim_on' : 'dim_off' })
      }
      sampleCounterRef.current += 1
      if (sampleCounterRef.current % Math.max(1, Math.round(1000 / ENERGY_CONFIG.tickMs)) === 0) {
        pushEvent({ ts: s.ts, type: 'energy_sample', energy: s.energy, speaking: s.speaking })
      }
    },
  })

  const handleCommit = useCallback(() => {
    if (hasCommittedRef.current) return
    hasCommittedRef.current = true
    if (commitTimerRef.current != null) { clearTimeout(commitTimerRef.current); commitTimerRef.current = null }
    pendingCommitRef.current = false
    const segments: ConfirmedSegment[] = transcriptRef.current
      .filter((t) => t.isFinal)
      .map((t) => ({ text: t.text.trim(), ts: t.ts }))
      .filter((s) => s.text)
    // include trailing unfinalized words — pressing commit often leaves the just-spoken answer interim
    const tail = interimRef.current.text.trim()
    if (tail) segments.push({ text: tail, ts: interimRef.current.ts })
    committedTranscriptRef.current = formatWithPauses(segments)
    committedAtRef.current = performance.now()
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[cryptic] committed transcript:', JSON.stringify(committedTranscriptRef.current))
    }
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'commit', value: committedTranscriptRef.current })

    // Trivial solve? (said the right answer, fast / near-wordless.) If so, swap the passive
    // reflection for the active instant-solve retrospective. Never in practice — the warm-up
    // stays minimal. Reads live refs to avoid stale-closure puzzle/mode.
    const p = puzzleRef.current
    const matched = p ? detectCrypticAnswer(committedTranscriptRef.current, p) : null
    const solveMs = committedAtRef.current - (puzzleStartRef.current?.perf ?? committedAtRef.current)
    const trivial =
      modeRef.current !== 'practice' &&
      isTrivialSolve({ committedTranscript: committedTranscriptRef.current, matchedAnswer: matched, solveMs })
    trivialSolveRef.current = trivial

    if (trivial) {
      matchedAtCommitRef.current = matched // the correct answer we heard → auto-credit after the retro
      solutionTypeRef.current = null
      setSolutionType(null)
      pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'retro_start' })
      // Flip the phase ref SYNCHRONOUSLY (not just via setState) so onTranscript's retro-done check
      // and the playing-guard see 'retro' the instant commit fires, before the render commits.
      phaseRef.current = 'retro'
      setPhase('retro')
    } else {
      pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'reflection_start' })
      phaseRef.current = 'reflection'
      setPhase('reflection')
    }
  }, [tsBase, pushEvent])

  // Commit BUTTON handler. If an utterance is still finalizing (an interim is showing — the "text
  // hasn't gone black yet"), wait for it to finalize before snapshotting, so we capture the COMPLETE
  // answer rather than a mid-revision interim (Web Speech interims are non-monotonic). Mirrors the
  // spoken-commit deferral: the next final fires handleCommit via onTranscript; a fallback timer
  // covers a stalled recogniser. Commits immediately when nothing is pending.
  const requestCommit = useCallback(() => {
    if (hasCommittedRef.current) return
    if (!interimRef.current.text.trim()) { handleCommit(); return }
    if (!pendingCommitRef.current) {
      pendingCommitRef.current = true
      commitTimerRef.current = window.setTimeout(() => { commitTimerRef.current = null; handleCommit() }, 1500)
    }
  }, [handleCommit])

  // ---- transcription ----
  const onTranscript = (entry: TranscriptEntry) => {
    // Post-commit speech (the passive reflection AND the trivial-solve retro) stays IN-STREAM,
    // exactly like the 24 game — untagged. The concurrent / reflection / retro split is recovered
    // from the commit / reflection_start / retro_start event timestamps, so there's no per-item tag
    // and no 24 inconsistency.
    transcriptRef.current.push(entry)
    energy.registerActivity()
    lastActivityRef.current = performance.now()
    if (entry.isFinal) {
      const text = entry.text.trim()
      if (text) setConfirmedSegments((prev) => [...prev, { text, ts: entry.ts }])
      setInterimText('')
      interimRef.current = { text: '', ts: 0 }
    } else {
      setInterimText(entry.text)
      interimRef.current = { text: entry.text, ts: entry.ts }
    }
    // In the retro, saying "done"/"commit" ends it; no puzzle-commit logic runs after commit.
    if (phaseRef.current === 'retro') {
      if (entry.isFinal && RETRO_DONE_WORD.test(entry.text)) endRetroRef.current()
      return
    }
    if (phaseRef.current !== 'playing') return
    // Defer a spoken "commit" until the utterance finalizes (Web Speech interim is non-monotonic and
    // truncates the answer if captured mid-utterance). Capture on the next final, with a fallback timer.
    if (pendingCommitRef.current && entry.isFinal) { handleCommit(); return }
    if (COMMIT_WORD.test(entry.text)) {
      if (entry.isFinal) handleCommit()
      else if (!pendingCommitRef.current) {
        pendingCommitRef.current = true
        commitTimerRef.current = window.setTimeout(() => { commitTimerRef.current = null; handleCommit() }, 1500)
      }
    }
  }
  const onSpeechStart = () => {
    energy.registerActivity()
    lastActivityRef.current = performance.now()
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'speech_start' })
  }
  const onSpeechEnd = () => pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'speech_end' })
  const onPermissionDenied = () => setPhase('mic-error')

  const speechActive = phase === 'playing' || phase === 'reflection' || phase === 'retro'
  useSpeechRecognition({
    active: speechActive && STT_ENGINE === 'webspeech',
    tsBase,
    onTranscript,
    onSpeechStart,
    onSpeechEnd,
    onPermissionDenied,
  })
  const streaming = useStreamingTranscription({
    active: speechActive && STT_ENGINE !== 'webspeech',
    tsBase,
    provider: STT_ENGINE === 'google' ? 'google' : 'deepgram',
    onTranscript,
    onSpeechStart,
    onSpeechEnd,
    onPermissionDenied,
  })

  // ---- per-puzzle lifecycle ----
  const startPuzzle = useCallback(
    (index: number) => {
      if (overDailyCap()) {
        setPhase('daily-limit')
        return
      }
      transcriptRef.current = []
      eventsRef.current = []
      committedTranscriptRef.current = ''
      hasCommittedRef.current = false
      interimRef.current = { text: '', ts: 0 }
      if (commitTimerRef.current != null) { clearTimeout(commitTimerRef.current); commitTimerRef.current = null }
      pendingCommitRef.current = false
      dimRef.current = false
      sampleCounterRef.current = 0
      trivialSolveRef.current = false
      matchedAtCommitRef.current = null
      solutionTypeRef.current = null
      setConfirmedSegments([])
      setInterimText('')
      setSolutionType(null)
      setOutcome(null)
      setShowPassConfirm(false)
      puzzleStartRef.current = { iso: new Date().toISOString(), perf: performance.now() }
      lastActivityRef.current = performance.now()
      pushEvent({ ts: Math.round(performance.now() - tsBaseRef.current), type: 'mic_on' })
      setPuzzleIndex(index)
      setPhase('playing')
    },
    [pushEvent]
  )

  // ---- mode entry ----
  // ONE unscored warm-up clue before the test — teaches the think-aloud-then-commit gesture.
  // Advances to the test regardless of outcome (see finishAdvance); never marks the test done.
  const beginPractice = useCallback(() => {
    track('labs_cryptic_practice_start')
    setMode('practice')
    sessionTracesRef.current = []
    setSessionPuzzles([pickCrypticPractice(variant.practice)])
    startPuzzle(0)
  }, [startPuzzle, variant.practice])

  const beginTest = useCallback(() => {
    track('labs_cryptic_test_start', { variant: variant.analyticsVariant })
    setMode('test')
    sessionTracesRef.current = []
    // 24-game model when the variant defines pre-determined groups; else per-band random draw.
    setSessionPuzzles(
      variant.conditions
        ? buildCrypticTestFromConditions(variant.puzzles, variant.conditions)
        : buildCrypticTest(variant.puzzles, TEST_LEN)
    )
    startPuzzle(0)
  }, [startPuzzle, variant.puzzles, variant.conditions, variant.analyticsVariant])

  const beginPlay = useCallback(() => {
    track('labs_cryptic_play_start', { variant: variant.analyticsVariant })
    setMode('play')
    sessionTracesRef.current = []
    setSessionPuzzles([pickCrypticPlay(variant.puzzles)]) // free play = random clues (no difficulty chooser)
    startPuzzle(0)
  }, [startPuzzle, variant.puzzles, variant.analyticsVariant])

  // ---- start / consent / mic ----
  const afterMic = useCallback(() => {
    if (overDailyCap()) setPhase('daily-limit')
    else if (isTestDone(variant.testDoneKey)) setPhase('mode-choice')
    // First-timers get the warm-up; anyone who's finished ANY labs game already knows the
    // think-aloud-then-commit gesture, so skip straight to the test.
    else if (hasCompletedAnyLabsTest()) setPhase('test-intro')
    else setPhase('practice-intro')
  }, [variant.testDoneKey])

  const requestMic = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((t) => t.stop())
      track('labs_mic_result', { granted: 'yes' })
      afterMic()
    } catch {
      track('labs_mic_result', { granted: 'no' })
      setPhase('mic-error')
    }
  }, [afterMic])

  const handleStart = useCallback(async () => {
    markConsented() // latch the one-time labs consent (skips the screen on future visits/games)
    const id = uuidv4()
    const startedAtISO = new Date().toISOString()
    const env = captureEnv()
    sessionRef.current = { id, startedAtISO }
    envRef.current = env
    setLabsUser({ labs_session_id: id, labs_stt_engine: STT_ENGINE })
    track('labs_cryptic_consent', { consented: 'yes' })
    const base = performance.now()
    tsBaseRef.current = base
    setTsBase(base)
    saveConsent({
      sessionId: id,
      consented: true,
      env,
      sttEngine: STT_ENGINE,
      source: SOURCE,
    }).catch(() => {})
    enrichCountry().then((c) => {
      if (c && envRef.current) envRef.current.country = c
    })
    await requestMic()
  }, [requestMic])

  // Returning player who already consented (this device) → skip the consent screen and start.
  useEffect(() => {
    if (hasConsented()) handleStart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---- reflection → answer entry ----
  const endReflection = useCallback(() => {
    if (!puzzle) return
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'reflection_end' })
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'mic_off' })
    reflectionEndRef.current = performance.now()
    setPhase('entry')
  }, [puzzle, tsBase, pushEvent])

  // Instant-solve retro finished (Done tapped, spoken "done", or the safety timeout) → close the
  // mic and auto-credit the answer we already heard at commit (a trivial solve always has one), so
  // the player isn't bounced back to re-type an answer they clearly got. Straight to the reveal.
  // reflectionDurationMs covers the retro.
  const endRetro = useCallback(() => {
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'retro_end' })
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'mic_off' })
    reflectionEndRef.current = performance.now()
    recordTrivialRef.current()
  }, [tsBase, pushEvent])
  endRetroRef.current = endRetro

  // ---- build + save one trace, then advance by mode ----
  const buildTrace = (answerMode: CrypticAnswerMode, typedRaw: string | null, matched: string | null, correct: boolean): CrypticTrace => {
    const p = puzzle!
    const pStart = puzzleStartRef.current!
    return {
      schemaVersion: CRYPTIC_TRACE_SCHEMA_VERSION,
      domain: variant.traceDomain,
      sessionId: sessionRef.current!.id,
      mode,
      trialIndex: puzzleIndex,
      sitting: 1,
      resumed: false,
      aborted: false,
      puzzleId: p.id,
      clue: p.clue,
      enumeration: p.enumeration,
      answer: p.answer,
      publisher: p.publisher,
      llmSolveRate: p.llmSolveRate,
      band: p.band,
      conditionId: p.conditionId ?? null,
      transcript: transcriptRef.current,
      events: eventsRef.current,
      committedTranscript: committedTranscriptRef.current,
      sttEngine: STT_ENGINE,
      answerMode,
      typedAnswer: typedRaw,
      matchedAnswer: matched,
      correct,
      correctnessReason: answerMode ?? 'aborted',
      puzzleStartedAt: pStart.iso,
      puzzleDurationMs: Math.min(Math.round(committedAtRef.current - pStart.perf), PUZZLE_LIMIT_MS),
      reflectionDurationMs: Math.round(reflectionEndRef.current - committedAtRef.current),
      trivialSolve: trivialSolveRef.current,
      solutionType: trivialSolveRef.current ? solutionTypeRef.current : null,
      env: envRef.current!,
      consented: true,
      source: SOURCE,
      firstExposure: !seenIds(variant.seenKey).has(p.id),
    }
  }

  const finishAdvance = () => {
    if (mode === 'practice') {
      // A single warm-up clue → straight into the test, whatever the outcome (voice "solved"
      // detection is fuzzier than typed input, so we never loop the warm-up).
      setPhase('test-intro')
    } else if (mode === 'test') {
      if (puzzleIndex < sessionPuzzles.length - 1) {
        startPuzzle(puzzleIndex + 1)
      } else {
        markTestDone(variant.testDoneKey)
        track('labs_cryptic_test_complete', { variant: variant.analyticsVariant, solved_count: sessionTracesRef.current.filter((t) => t.correct).length })
        setPhase('summary')
      }
    } else {
      // play: load another clue and keep going
      setSessionPuzzles((prev) => [...prev, pickCrypticPlay(variant.puzzles)])
      startPuzzle(puzzleIndex + 1)
    }
  }

  // Save the trace (data safe immediately), then show the reveal screen. Advancing to the
  // next clue happens on the reveal's Continue button (finishAdvance), so the player always
  // gets to see the answer + explanation — however the clue ended.
  const recordOutcome = (answerMode: CrypticAnswerMode, typedRaw: string | null, matched: string | null, correct: boolean) => {
    const p = puzzle!
    const trace = buildTrace(answerMode, typedRaw, matched, correct) // reads firstExposure before we mark it
    markSeen(variant.seenKey, p.id)
    sessionTracesRef.current = [...sessionTracesRef.current, trace]
    addStreamedMs(trace.puzzleDurationMs + trace.reflectionDurationMs)
    saveCrypticTrace(trace as unknown as { domain: 'cryptic' | 'cryptic-easy'; sessionId: string } & Record<string, unknown>)
      .then((r) => {
        // eslint-disable-next-line no-console
        console.log(`[cryptic] ${mode} #${trace.trialIndex} → ${r.collection}${r.fellBack ? ' (fallback)' : ''}`, r.id)
      })
      .catch((e: any) => {
        // eslint-disable-next-line no-console
        console.error('[cryptic] save failed', e)
        setSaveError(e?.message || 'save failed')
      })

    track('labs_cryptic_puzzle_end', {
      variant: variant.analyticsVariant,
      mode,
      trial_index: puzzleIndex,
      outcome: answerMode === 'timeout' ? 'timeout' : answerMode === 'pass' ? 'pass' : correct ? 'solved' : 'wrong',
      correct: correct ? 'yes' : 'no',
      stt_engine: STT_ENGINE,
      spoke: (trace.committedTranscript || '').trim() ? 'yes' : 'no',
    })

    setOutcome({ answerMode, correct, typed: typedRaw })
    setPhase('reveal')
  }

  // A trivial solve is auto-credited from the answer we heard at commit (voice-detected), so the
  // retro's Done goes straight to the reveal. Kept in a ref because endRetro is defined above this.
  recordTrivialRef.current = () => recordOutcome('voice-detected', null, matchedAtCommitRef.current, true)

  // End an unsolved clue — voluntary give-up ('pass') or the 3-minute limit ('timeout').
  const endUnsolved = (reason: 'pass' | 'timeout') => {
    if (hasCommittedRef.current) return
    hasCommittedRef.current = true
    setShowPassConfirm(false)
    const segments: ConfirmedSegment[] = transcriptRef.current
      .filter((t) => t.isFinal)
      .map((t) => ({ text: t.text.trim(), ts: t.ts }))
      .filter((s) => s.text)
    const tail = interimRef.current.text.trim()
    if (tail) segments.push({ text: tail, ts: interimRef.current.ts })
    committedTranscriptRef.current = formatWithPauses(segments)
    const now = performance.now()
    committedAtRef.current = now
    reflectionEndRef.current = now
    pushEvent({ ts: Math.round(now - tsBase), type: reason === 'timeout' ? 'timeout' : 'answer_passed' })
    pushEvent({ ts: Math.round(now - tsBase), type: 'mic_off' })
    recordOutcome(reason, null, null, false)
  }
  endUnsolvedRef.current = endUnsolved

  const submitTyped = (raw: string) => {
    if (!puzzle) return
    const verdict = checkCrypticAnswer(raw, puzzle)
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'answer_typed', value: raw })
    recordOutcome('typed', raw, verdict.matched, verdict.correct)
  }

  const continueLabel =
    mode === 'practice' ? 'Start the test' : mode === 'test' ? (isLastInSession ? 'See your results' : 'Next clue') : 'Next clue'

  // ---- render ----
  const renderScreen = () => {
    if (STT_ENGINE === 'webspeech' && gateReason !== 'ok') return <BrowserGate reason={gateReason} game="cryptic" />

    switch (phase) {
      case 'consent':
        if (hasConsented()) return null // auto-starting via the mount effect
        return (
          <CrypticConsent
            onConsent={handleStart}
            heading={variant.consentHeading}
            sub={variant.consentSub}
            example={variant.consentExample}
          />
        )

      case 'mic-error':
        return (
          <StatusScreen
            title="We couldn’t access your mic"
            body="This game needs microphone access to hear you think out loud. Check your browser’s mic permission for this site, then try again."
            action={{ label: 'Try again', onClick: requestMic }}
          />
        )

      case 'daily-limit':
        return (
          <StatusScreen
            title="That’s your play for today"
            body="You’ve reached today’s voice-play limit — it resets tomorrow. Thanks for helping our research!"
          />
        )

      case 'mode-choice':
        return (
          <div className="flex flex-col items-center justify-center px-6 pt-16 pb-8 text-center">
            <Brand game="cryptic" />
            <h2 className="mt-8 text-xl font-semibold text-gray-800">Welcome back</h2>
            <p className="mt-2 max-w-sm text-gray-600">You’ve already done the cryptic test. Jump into free play, or take it again.</p>
            <button type="button" onClick={beginPlay} className="mt-6 w-full max-w-xs rounded-lg bg-[#820458] py-3 text-white font-semibold">
              Free play
            </button>
            <button type="button" onClick={beginTest} className="mt-3 text-sm text-gray-500 underline">
              Take the test again
            </button>
            <div className="w-full max-w-xs">
              <OtherGamesLinks current="cryptic" />
            </div>
          </div>
        )

      case 'practice-intro':
        return (
          <StatusScreen
            title="Quick warm-up"
            body={
              <>
                One practice clue first — it won’t count. The idea is simple:{' '}
                <strong className="font-semibold text-gray-800">say what you’re thinking out loud</strong> as you work it out,
                then say “commit” (or tap the button) when you’ve got it. Then we’ll start the real test.
              </>
            }
            action={{ label: 'Start warm-up', onClick: beginPractice }}
          />
        )

      case 'test-intro':
        return (
          <StatusScreen
            title={`${TEST_LEN} cryptic clues`}
            body={
              <>
                {TEST_LEN} {variant.testIntroBody}{' '}
                <strong className="font-semibold text-gray-800">You have up to 3 minutes for each.</strong> Think out loud as
                you work each one through — finishing all {TEST_LEN} unlocks free play.
              </>
            }
            action={{ label: 'Start', onClick: beginTest }}
          />
        )

      case 'playing': {
        if (!puzzle) return null
        const showNudge = performance.now() - lastActivityRef.current > NUDGE_SILENCE_MS
        return (
          <div key={puzzleIndex} className="labs-puzzle-fade">
            <CrypticView
              clue={puzzle.clue}
              energy={energy.energy}
              speaking={energy.speaking}
              dimmed={energy.dimmed}
              confirmedSegments={confirmedSegments}
              interimText={interimText}
              progressLabel={progressLabel}
              showNudge={showNudge}
              onCommit={requestCommit}
              onPass={() => {
                pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'pass_prompt' })
                setShowPassConfirm(true)
              }}
            />
            {showPassConfirm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6">
                <div className="w-full max-w-xs rounded-xl bg-white p-5 text-center shadow-xl">
                  <h3 className="font-semibold text-gray-800">Give up on this one?</h3>
                  <p className="mt-1 text-sm text-gray-500">You’ve got up to 3 minutes — talking it through a little longer often cracks it. Keep going?</p>
                  <button type="button" onClick={() => setShowPassConfirm(false)} className="mt-4 w-full rounded-lg bg-[#398874] py-3 font-semibold text-white">
                    Keep going
                  </button>
                  <button type="button" onClick={() => endUnsolved('pass')} className="mt-2 w-full rounded-lg border border-gray-200 py-2.5 text-sm font-medium text-gray-500 hover:bg-gray-50">
                    Give up
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      }

      case 'reflection':
        if (!puzzle) return null
        return (
          <CrypticStage clue={puzzle.clue} confirmedSegments={confirmedSegments} interimText={interimText} progressLabel={progressLabel}>
            <ReflectionNotice seconds={REFLECTION_SECONDS} onDone={endReflection} />
          </CrypticStage>
        )

      case 'retro':
        if (!puzzle) return null
        return (
          <CrypticStage clue={puzzle.clue} confirmedSegments={confirmedSegments} interimText={interimText} progressLabel={progressLabel}>
            <InstantRetro
              answer={puzzle.answer}
              solutionType={solutionType}
              onPickType={(t) => {
                solutionTypeRef.current = t
                setSolutionType(t)
              }}
              onDone={endRetro}
            />
          </CrypticStage>
        )

      case 'entry': {
        if (!puzzle) return null
        const suggested = detectCrypticAnswer(committedTranscriptRef.current, puzzle)
        return (
          <CrypticStage clue={puzzle.clue} confirmedSegments={confirmedSegments} interimText={interimText} progressLabel={progressLabel}>
            <CrypticAnswer
              enumeration={puzzle.enumeration}
              suggested={suggested}
              onSubmit={(a) => submitTyped(a)}
              onPass={() => {
                pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'answer_passed' })
                recordOutcome('pass', null, null, false)
              }}
            />
          </CrypticStage>
        )
      }

      case 'reveal': {
        if (!puzzle || !outcome) return null
        const { answerMode, correct, typed } = outcome
        const head =
          (answerMode === 'typed' || answerMode === 'voice-detected') && correct
            ? { kind: 'solved' as const, title: 'Correct!' }
            : answerMode === 'typed'
            ? { kind: 'wrong' as const, title: 'Not quite' }
            : answerMode === 'timeout'
            ? { kind: 'timeout' as const, title: 'Time’s up' }
            : { kind: null, title: 'Here’s the answer' }
        const meaning = (puzzle.definition || '').replace(/\.$/, '') // gloss, trailing period trimmed
        const deviceLabel = puzzle.device ? DEVICE_LABEL[puzzle.device] : undefined
        const hasExplanation = !!(meaning || puzzle.wordplay)
        return (
          <CrypticStage
            clue={puzzle.clue}
            defClue={puzzle.defClue}
            showDef
            confirmedSegments={confirmedSegments}
            interimText={interimText}
            progressLabel={progressLabel}
          >
            <div className="text-center">
              {head.kind && <OutcomeIcon kind={head.kind} />}
              <div className="mt-1.5 font-semibold text-gray-800">{head.title}</div>

              <div className="mt-3 text-[0.68rem] uppercase tracking-wider text-gray-400">Answer</div>
              <div className="mt-1 inline-block rounded-lg bg-[#398874]/10 px-4 py-2 text-lg font-extrabold tracking-wide text-[#398874]">
                {puzzle.answer} <span className="font-normal text-[#398874]/60">{puzzle.enumeration}</span>
              </div>
              {answerMode === 'typed' && !correct && typed && (
                <p className="mt-2 text-sm text-gray-500">You said “{typed}”.</p>
              )}

              {hasExplanation && (
                <div className="mt-4 rounded-lg bg-gray-50 px-4 py-3 text-left">
                  {meaning && (
                    <div className="grid grid-cols-[76px_1fr] gap-2">
                      <div className="pt-0.5 text-[0.68rem] font-bold uppercase tracking-wide text-gray-400">Meaning</div>
                      <div className="text-sm text-gray-700">
                        <span className={DEF_UNDERLINE}>{meaning}</span>
                      </div>
                    </div>
                  )}
                  {puzzle.wordplay && (
                    <div className={`grid grid-cols-[76px_1fr] gap-2 ${meaning ? 'mt-2.5' : ''}`}>
                      <div className="flex flex-col items-start gap-1.5 pt-0.5">
                        <span className="text-[0.68rem] font-bold uppercase tracking-wide text-gray-400">Wordplay</span>
                        {deviceLabel && (
                          <span className="rounded-full bg-[#820458]/10 px-2 py-0.5 text-[0.56rem] font-bold uppercase tracking-wide text-[#820458]">
                            {deviceLabel}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-700">{puzzle.wordplay}</div>
                    </div>
                  )}
                </div>
              )}

              <div ref={revealBtnRef}>
                <button
                  type="button"
                  onClick={finishAdvance}
                  className="mt-5 w-full rounded-lg bg-[#820458] py-3 text-white font-semibold"
                >
                  {continueLabel}
                </button>
                {mode === 'play' && <OtherGamesLinks current="cryptic" />}
              </div>
            </div>
          </CrypticStage>
        )
      }

      case 'summary': {
        const traces = sessionTracesRef.current
        const solved = traces.filter((t) => t.correct).length
        const outcomeOf = (t: CrypticTrace) =>
          t.correct
            ? { kind: 'solved' as const, label: 'Solved', cls: 'text-[#398874]' }
            : t.answerMode === 'timeout'
            ? { kind: 'timeout' as const, label: 'Ran out of time', cls: 'text-gray-400' }
            : t.answerMode === 'pass'
            ? { kind: null, label: 'Passed', cls: 'text-gray-400' }
            : { kind: 'wrong' as const, label: 'Not solved', cls: 'text-[#820458]' }
        return (
          <div className="flex flex-col items-center px-6 pt-10 pb-8">
            <Brand game="cryptic" />
            <div className="mt-8 w-full max-w-md text-center">
              <h2 className="text-xl font-semibold text-gray-800">
                You solved <span className="text-[#398874]">{solved}</span> of {traces.length}
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                Thanks for thinking out loud — every session helps us compare how people and AI crack these clues.
              </p>

              <ul className="mt-5 divide-y divide-gray-100 rounded-lg border border-gray-100 text-left">
                {traces.map((t, i) => {
                  const o = outcomeOf(t)
                  return (
                    <li key={i} className="flex items-center justify-between gap-3 px-4 py-3">
                      <span className="font-medium text-gray-700">
                        {t.answer} <span className="text-xs font-normal text-gray-400">{t.enumeration}</span>
                      </span>
                      <span className={`flex items-center gap-1.5 text-sm font-medium ${o.cls}`}>
                        {o.label}
                        {o.kind && <OutcomeIcon kind={o.kind} className="h-5 w-5" />}
                      </span>
                    </li>
                  )
                })}
              </ul>

              {saveError && <p className="mt-3 text-xs text-red-500">Some answers may not have saved: {saveError}</p>}

              {sessionRef.current && !surveyCompletedAt(variant.survey.id) && !surveyDismissed && (
                <div className="mt-5 rounded-lg border border-[#820458]/20 bg-[#820458]/5 px-4 py-3 text-center">
                  <p className="text-sm font-medium text-gray-700">Got 2 minutes? A little feedback really helps our research.</p>
                  <button
                    type="button"
                    onClick={() => { track('labs_cryptic_survey_view'); setPhase('survey') }}
                    className="mt-2 w-full rounded-lg bg-[#820458] py-2.5 text-sm font-semibold text-white"
                  >
                    Give feedback
                  </button>
                  <button
                    type="button"
                    onClick={() => { markSurveyDone(variant.survey.id); setSurveyDismissed(true) }}
                    className="mt-1.5 text-xs text-gray-400 underline"
                  >
                    No thanks
                  </button>
                </div>
              )}

              <button type="button" onClick={beginPlay} className="mt-6 w-full rounded-lg bg-[#820458] py-3 font-semibold text-white">
                Keep playing (free play)
              </button>
              <OtherGamesLinks current="cryptic" />
            </div>
          </div>
        )
      }

      case 'survey':
        if (!sessionRef.current) return null
        return (
          <SurveyScreen
            schema={variant.survey}
            sessionId={sessionRef.current.id}
            gameType={variant.surveyGameType}
            onDone={() => setPhase('summary')}
          />
        )

      default:
        return null
    }
  }

  return <AdLayout debugInfo={{ audioSeconds: streaming.audioSeconds }}>{renderScreen()}</AdLayout>
}

export default GameCryptic
